import React, { useState } from 'react';
import { NavLink, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { User as UserIcon, Package, LogOut, Mail, Phone, Check } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { Orders } from './Orders';
import { toast } from 'sonner';
function ProfileTab() {
  const {
    user,
    updateProfile
  } = useAuth();
  const [form, setForm] = useState({
    firstName: user?.firstName || '',
    lastName: user?.lastName || '',
    email: user?.email || '',
    phone: user?.phone || ''
  });
  const [saved, setSaved] = useState(false);
  const update = (field: string, value: string) => setForm(prev => ({
    ...prev,
    [field]: value
  }));
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    updateProfile({
      firstName: form.firstName,
      lastName: form.lastName,
      phone: form.phone
    });
    setSaved(true);
    toast.success('Profile updated');
    setTimeout(() => setSaved(false), 2000);
  };
  return <div data-id="element-249">
      <h2 className="text-xl font-bold text-gray-900 mb-1" data-id="element-250">My Profile</h2>
      <p className="text-sm text-gray-500 mb-6" data-id="element-251">
        Manage your personal information.
      </p>

      <form onSubmit={handleSubmit} className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 space-y-4" data-id="element-252">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-253">
          <div data-id="element-254">
            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-255">
              First name
            </label>
            <input type="text" required value={form.firstName} onChange={e => update('firstName', e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent" data-id="element-256" />
          </div>
          <div data-id="element-257">
            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-258">
              Last name
            </label>
            <input type="text" required value={form.lastName} onChange={e => update('lastName', e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent" data-id="element-259" />
          </div>
        </div>

        <div data-id="element-260">
          <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-261">
            Email
          </label>
          <div className="relative" data-id="element-262">
            <Mail className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" data-id="element-263" />
            <input type="email" disabled value={form.email} className="w-full pl-9 pr-4 py-2 border border-gray-200 bg-gray-50 text-gray-500 rounded-lg" data-id="element-264" />
          </div>
          <p className="text-xs text-gray-400 mt-1" data-id="element-265">
            Email cannot be changed. Contact support if needed.
          </p>
        </div>

        <div data-id="element-266">
          <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-267">
            Phone
          </label>
          <div className="flex" data-id="element-268">
            <span className="inline-flex items-center px-3 rounded-l-lg border border-r-0 border-gray-300 bg-gray-50 text-gray-500 text-sm" data-id="element-269">
              +960
            </span>
            <input type="tel" required value={form.phone} onChange={e => update('phone', e.target.value)} className="flex-1 min-w-0 px-3 py-2 rounded-r-lg border border-gray-300 focus:ring-2 focus:ring-red-500 focus:border-transparent" data-id="element-270" />
          </div>
        </div>

        <div className="pt-2" data-id="element-271">
          <button type="submit" className="px-6 py-2.5 bg-red-600 text-white rounded-lg font-medium hover:bg-red-700 transition-colors inline-flex items-center gap-2" data-id="element-272">
            {saved ? <>
                <Check className="w-4 h-4" data-id="element-273" /> Saved
              </> : 'Save Changes'}
          </button>
        </div>
      </form>
    </div>;
}
export function Account() {
  const {
    user,
    logout,
    isAuthenticated
  } = useAuth();
  const navigate = useNavigate();
  if (!isAuthenticated || !user) {
    return <Navigate to="/login" state={{
      from: '/account'
    }} replace data-id="element-274" />;
  }
  const handleLogout = () => {
    logout();
    toast.success('Signed out');
    navigate('/');
  };
  const linkClass = ({
    isActive
  }: {
    isActive: boolean;
  }) => `flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm font-medium transition-colors ${isActive ? 'bg-red-50 text-red-600' : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'}`;
  return <div className="bg-gray-50 min-h-screen pb-16" data-id="element-275">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10" data-id="element-276">
        <div className="mb-8" data-id="element-277">
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900" data-id="element-278">
            Hi, {user.firstName} 👋
          </h1>
          <p className="text-gray-500 text-sm mt-1" data-id="element-279">
            Manage your account, orders, and details.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6" data-id="element-280">
          {/* Sidebar */}
          <aside className="lg:col-span-3" data-id="element-281">
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-3 space-y-1 sticky top-24" data-id="element-282">
              <NavLink to="/account" end className={linkClass} data-id="element-283">
                <UserIcon className="w-4 h-4" data-id="element-284" /> My Profile
              </NavLink>
              <NavLink to="/account/orders" className={linkClass} data-id="element-285">
                <Package className="w-4 h-4" data-id="element-286" /> My Orders
              </NavLink>
              <button type="button" onClick={handleLogout} className="w-full flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm font-medium text-gray-600 hover:bg-gray-50 hover:text-red-600 transition-colors" data-id="element-287">
                <LogOut className="w-4 h-4" data-id="element-288" /> Sign Out
              </button>
            </div>
          </aside>

          {/* Content */}
          <section className="lg:col-span-9" data-id="element-289">
            <Routes data-id="element-290">
              <Route index element={<ProfileTab data-id="element-292" />} data-id="element-291" />
              <Route path="orders" element={<Orders data-id="element-294" />} data-id="element-293" />
              <Route path="*" element={<Navigate to="/account" replace data-id="element-296" />} data-id="element-295" />
            </Routes>
          </section>
        </div>
      </div>
    </div>;
}